<template>
  <v-layout style="background: #f5f6fa">
    <the-header v-model:isExpanded="isExpanded"></the-header>
    <b-sidebar-menu
      v-if="isExpanded"
      :expanded="false"
      :items="customMenuItems"
      :list-props="{ elevation: 0, border: 0 }"
      :loading="!user"
      :user="user"
      width="271"
      style="background: white">
    </b-sidebar-menu>
    <v-main style="min-height: 100vh">
      <div class="page h-100">
        <Breadcrumb />
        <div class="h-100">
          <RouterView :key="renderKey" />
        </div>
      </div>
    </v-main>
  </v-layout>
</template>

<script>
import TheHeader from '@/components/the-header/TheHeader.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import { useRoute } from '@/plugins'
import { computed, ref, watch, provide, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { socket, documentCountsChannel } from '@/socket'
import { BSidebarMenu } from 'best-modules/components'
import {
  administrationMenuItems,
  auditMenuItems,
  getDefaultMenuItems,
  operationRiskMenuItems,
  taskMenuItems,
  documentAgreementMenuItems,
} from '@/router/menuItems.ts'
import { getPersonName } from 'best-modules/utils/helpers'
import { rerenderKey } from '@/injectionKeys'
import { debounce } from 'lodash'

export default {
  components: {
    TheHeader,
    Breadcrumb,
    BSidebarMenu,
  },
  setup() {
    const route = useRoute()
    const store = useStore()
    const user = computed(() => store.state.user.userData)
    const isExpanded = ref(true)

    store.dispatch('user/getUserData')
    store.dispatch('documentCounts/getDocumentCounts')

    const refetchDocumentCounts = () =>
      store.dispatch('documentCounts/getDocumentCounts')
    socket.on(documentCountsChannel, refetchDocumentCounts)
    onUnmounted(() => socket.off(documentCountsChannel, refetchDocumentCounts))

    const documentCounts = computed(() => store.state.documentCounts)
    const renderKey = ref(1)

    const rerender = debounce(() => {
      console.log('rerender')
      renderKey.value += 1
    }, 100)

    provide(rerenderKey, rerender)

    watch(
      computed(() => {
        const r = route.matched.find(r => r.meta.tabList) || route

        return r ? `${route.params.id || route.params.uuid || 0}` : null
      }),
      value => {
        if (value) rerender()
      }
    )

    const customMenuItems = computed(() => {
      const arr = []
      if (route.matched.some(r => r.name === 'administration')) {
        arr.push(...administrationMenuItems)
      } else if (route.matched.some(r => r.name === 'audit-section')) {
        arr.push(...auditMenuItems)
      } else if (route.matched.some(r => r.name === 'operation-risk')) {
        arr.push(...operationRiskMenuItems)
      } else if (route.matched.some(r => ['tasks', 'task'].includes(r.name))) {
        arr.push(...taskMenuItems)
      } else if (route.matched.some(r => r.name === 'document-agreement')) {
        arr.push(...documentAgreementMenuItems)
      } else {
        arr.push(...getDefaultMenuItems(documentCounts.value))
      }

      if (user?.value?.absence_by_deputy?.length) {
        arr.push({
          label: 'Заміщення',
          icon: 'mdi-account-multiple-check-outline',
          children: user.value.absence_by_deputy.map(item => ({
            label: getPersonName(item.user, { initials: true }),
            children: [
              {
                label: 'На візуванні',
                to: {
                  name: 'absence-document-agreement-me-list',
                  params: { absenceId: +item.userId },
                },
              },
              {
                label: 'На підписанні',
                to: {
                  name: 'absence-document-signature-all-list',
                  params: { absenceId: +item.userId },
                },
              },
              {
                label: 'Вхідні завдання',
                to: {
                  name: 'absence-incoming-tasks',
                  params: { absenceId: +item.userId },
                },
              },
            ],
          })),
        })
      }

      if (user?.value?.active_delegated_to_me?.length) {
        arr.push({
          label: 'Делегування',
          icon: 'mdi-account-switch',
          children: user.value.active_delegated_to_me.map(item => ({
            label: getPersonName(item.user, { initials: true }),
            to: {
              name: 'delegation-single',
              params: { id: item.id },
            },
          })),
        })
      }

      return arr
    })

    return { renderKey, customMenuItems, user, isExpanded }
  },
}
</script>

<style scoped>
@keyframes open {
  0% {
    display: none;
  }
  100% {
    display: inline-block;
  }
}

@keyframes close {
  0% {
    display: inline-block;
  }
  100% {
    display: none;
  }
}
</style>
